import {
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useElement,
    useElements,
    useListen,
    useProp,
} from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { Image } from '@atoms/image/image';
import { ButtonRound } from '@source/components/atoms/button-round/button-round';
import { ImageSettings } from '@source/components/atoms/image/utilities/image';

export interface ZoomImageInterface {
    zoomIn: Function;
    zoomOut: Function;
}

interface Size {
    width: number;
    height: number;
}

interface Distance {
    x: number;
    y: number;
}

interface Setting {
    elementBounds?: ClientRect;
    diff?: Size;
    zoomedSize?: Size;
    zoomLevel: number;
}

const ZOOMED_CLASS = 'zoom-image--zoomed';

const MIN_ZOOM_LEVEL = 2;
const MAX_ZOOM_LEVEL = 4;
const ZOOM_STEP = 1;

const ZoomImage: FC = ({ element }) => {
    const [settings] = useProp<ImageSettings | null>('settings', null);
    const [url] = useProp<string | null>('url', null);
    const [alt] = useProp<string | null>('alt', null);

    const img = useElement<HTMLImageElement>('img');
    const container = useElement<HTMLButtonElement>('[js-hook-zoom-image-container]');
    const buttons = useElements<HTMLButtonElement>('[js-hook-zoom-image-button]');

    let isZoomed = false;
    let prevSetting: Setting | undefined;
    let setting: Setting = {
        zoomLevel: 0,
    };

    useBindMethod('zoomIn', zoomIn);
    useBindMethod('zoomOut', zoomOut);

    useListen(element, 'mousemove', (e: MouseEvent) => {
        if (isZoomed) {
            setScroll(setting, { useMouseEvent: e });
        }
    });

    useListen(buttons, 'mousemove', (e: MouseEvent) => {
        e.stopPropagation();
    });

    onDidLoad(() => {
        setting.elementBounds = getElementBounds();
    });

    function zoomIn(e?: MouseEvent) {
        setting = zoomInSetting(setting);

        if (!setting.zoomedSize || !img.current) return;

        element.classList.add(ZOOMED_CLASS);

        img.current.style.width = `${setting.zoomedSize.width}px`;
        img.current.style.height = `${setting.zoomedSize.height}px`;

        // increase image quality when zooming in
        img.current.src = img.current.src.replace(/\?.*/gi, '');
        img.current.srcset = img.current.srcset.replace(/\?.*/gi, ' 1920w');

        setScroll(setting, {
            useMouseEvent: e,
            useCurrentScroll: !e,
        });

        if (!isZoomed) {
            isZoomed = true;
        }
    }

    function zoomOut(reset = false) {
        setting = zoomOutSetting(setting, reset);

        const { zoomLevel, zoomedSize } = setting;

        if (zoomLevel === 0) {
            element.classList.remove(ZOOMED_CLASS);

            if (img.current) {
                img.current.style.width = '';
                img.current.style.height = '';
            }

            isZoomed = false;
        } else {
            setScroll(setting, {
                useCurrentScroll: true,
            });

            if (zoomedSize && img.current) {
                img.current.style.width = `${zoomedSize.width}px`;
                img.current.style.height = `${zoomedSize.height}px`;
            }
        }
    }

    function zoomInSetting(setting: Setting): Setting {
        prevSetting = { ...setting };

        setting.zoomLevel = increaseZoomLevel(setting.zoomLevel);
        setting.elementBounds = getElementBounds();
        setting.zoomedSize = getZoomedImgSize(setting);
        setting.diff = getDiff(setting);

        return setting;
    }

    function zoomOutSetting(setting: Setting, reset?: boolean): Setting {
        prevSetting = { ...setting };

        setting.zoomLevel = decreaseZoomLevel(reset ? 0 : setting.zoomLevel);

        setting.zoomedSize = getZoomedImgSize(setting);
        setting.diff = getDiff(setting);

        return setting;
    }

    function increaseZoomLevel(level: number) {
        level += ZOOM_STEP;
        level = level > MAX_ZOOM_LEVEL ? MAX_ZOOM_LEVEL : level;
        level = level < MIN_ZOOM_LEVEL ? MIN_ZOOM_LEVEL : level;

        return level;
    }

    function decreaseZoomLevel(level: number) {
        level -= ZOOM_STEP;
        level = level < MIN_ZOOM_LEVEL ? 0 : level;

        return level;
    }

    function setScroll(
        setting: Setting,
        options?: {
            useMouseEvent?: MouseEvent;
            useCurrentScroll?: boolean;
        },
    ) {
        const { diff } = setting;

        if (!diff) return;

        let currentDistance = {
            x: 0,
            y: 0,
        };

        if (options?.useMouseEvent) {
            currentDistance = getDistanceByMouse(setting, options.useMouseEvent);
        } else if (options?.useCurrentScroll) {
            currentDistance = prevSetting ? getDistanceByScroll(prevSetting) : currentDistance;
        }
        container.current!.scrollLeft = diff.width * currentDistance.x;
        container.current!.scrollTop = diff.height * currentDistance.y;
    }

    function getDistanceByMouse(setting: Setting, e: MouseEvent): Distance {
        const { diff, elementBounds } = setting;

        if (!diff || !elementBounds || !container.current) return { x: 0, y: 0 };

        const mouseX = e.clientX;
        const mouseY = e.clientY;

        return {
            x: (mouseX - elementBounds.left) / elementBounds.width,
            y: (mouseY - elementBounds.top) / elementBounds.height,
        };
    }

    function getDistanceByScroll(setting: Setting): Distance {
        const { scrollTop, scrollLeft } = container.current!;

        const { diff } = setting;

        return {
            x: diff ? scrollLeft / diff.width : 0,
            y: diff ? scrollTop / diff.height : 0,
        };
    }

    function getElementBounds() {
        return element.getBoundingClientRect();
    }

    function getZoomedImgSize(setting: Setting): Size | undefined {
        const { elementBounds, zoomLevel } = setting;

        if (!img.current || !elementBounds) return;

        let zoomedHeight: number | undefined;
        let zoomedWidth: number | undefined;

        const itemRatio = elementBounds.height / elementBounds.width;
        const naturalRatio = img.current.naturalHeight / img.current.naturalWidth;

        if (itemRatio < naturalRatio) {
            zoomedHeight = elementBounds.height * zoomLevel;
            zoomedWidth = zoomedHeight / naturalRatio;
        } else {
            zoomedWidth = elementBounds.width * zoomLevel;
            zoomedHeight = zoomedWidth * naturalRatio;
        }

        return {
            width: zoomedWidth < elementBounds.width ? elementBounds.width : zoomedWidth,
            height: zoomedHeight < elementBounds.height ? elementBounds.height : zoomedHeight,
        };
    }

    function getDiff(setting: Setting): Size | undefined {
        const { zoomedSize, elementBounds } = setting;

        if (!zoomedSize || !elementBounds) return;

        return {
            width: zoomedSize.width - elementBounds.width,
            height: zoomedSize.height - elementBounds.height,
        };
    }

    return (
        <Fragment>
            {settings && url && alt && (
                <Fragment>
                    <div class="zoom-image__button-container">
                        <ButtonRound
                            ariaLabel="Zoom in"
                            size="small"
                            classes="zoom-image__zoom-button"
                            svg="icons-plus"
                            hook="zoom-image-button"
                            onClick={(e: MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                zoomIn();
                            }}
                        />
                        <ButtonRound
                            ariaLabel="Zoom uit"
                            size="small"
                            classes="zoom-image__zoom-button"
                            svg="icons-minus"
                            hook="zoom-image-button"
                            onClick={(e: MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                zoomOut();
                            }}
                        />
                    </div>

                    <div
                        role="button"
                        tabindex="-1"
                        class="zoom-image__container"
                        js-hook-zoom-image-container
                        onClick={(e: MouseEvent) => {
                            e.preventDefault();

                            if (!isZoomed) {
                                zoomIn(e);
                            } else {
                                zoomOut(true);
                            }
                        }}>
                        <Image
                            classes="zoom-image__image"
                            image={url}
                            mode="max"
                            alt={alt}
                            settings={settings}
                        />
                    </div>
                </Fragment>
            )}
        </Fragment>
    );
};

ZoomImage.props = {
    settings: {
        type: Object,
        required: true,
    },
    url: {
        type: String,
        required: true,
    },
    alt: {
        type: String,
        required: true,
    },
};

defineElement('zoom-image', ZoomImage);
